<template>
  <div class="container">
    <h2 class="title">📘 Student Enrollments</h2>

    <div class="filter-bar">
      <button class="btn add-btn" @click="openModal">
        <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 mr-1" viewBox="0 0 20 20" fill="currentColor">
          <path fill-rule="evenodd" d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z" clip-rule="evenodd" />
        </svg>
        Enroll Student
      </button>
    </div>

    <div class="table-container" v-if="enrollments.length > 0">
      <table class="modern-table">
        <thead>
          <tr>
            <th>Student</th>
            <th>Class</th>
            <th>Section</th>
            <th>Academic Year</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="enrollment in enrollments" :key="enrollment.id">
            <td>
              <div class="flex items-center">
                <div class="avatar-placeholder mr-2">
                  {{ getInitials(enrollment.student?.name) }}
                </div>
                <div>
                  <p class="font-medium">{{ enrollment.student?.name }}</p>
                  <p class="text-xs text-gray-400">{{ enrollment.student?.srn || '—' }}</p>
                </div>
              </div>
            </td>
            <td>{{ enrollment.class?.name || '—' }}</td>
            <td>{{ enrollment.section?.name || '—' }}</td>
            <td>{{ enrollment.study_year?.year_study || '—' }}</td>
            <td>
              <button class="btn edit-btn" @click="edit(enrollment)">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 mr-1" viewBox="0 0 20 20" fill="currentColor">
                  <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                </svg>
                Edit
              </button>
              <button class="btn delete-btn" @click="remove(enrollment.id)">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 mr-1" viewBox="0 0 20 20" fill="currentColor">
                  <path fill-rule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clip-rule="evenodd" />
                </svg>
                Delete
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div v-else class="no-results">
      <svg xmlns="http://www.w3.org/2000/svg" class="h-10 w-10 text-gray-500 mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
      </svg>
      <p>No enrollments found</p>
      <button class="btn add-btn mt-4" @click="openModal">
        Enroll your first student
      </button>
    </div>

    <!-- Enrollment Modal -->
<!-- Enrollment Modal -->
<div v-if="showModal" class="modal-overlay">
  <div class="modal-content">
    <div class="modal-header">
      <h3>{{ editingId ? 'Edit Enrollment' : 'New Enrollment' }}</h3>
      <button class="close-btn" @click="closeModal">&times;</button>
    </div>
    <div class="modal-body">
      <form @submit.prevent="submit">
        <div class="form-group">
          <label>Student</label>
          <div class="student-select-container">
            <div 
              class="search-container" 
              @click.stop="toggleDropdown"
            >
            <input 
  type="text" 
  v-model="studentSearch" 
  @input="searchStudents" 
  @click.stop="showDropdown = true"
  placeholder="Search students..."
  class="search-input"
>
              <svg xmlns="http://www.w3.org/2000/svg" class="search-icon" viewBox="0 0 20 20" fill="currentColor">
                <path fill-rule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clip-rule="evenodd" />
              </svg>
            </div>
            <div class="select-dropdown" v-if="showDropdown">
              <div class="dropdown-content">
                <div v-if="filteredStudents.length === 0" class="no-results">
                  No students found
                </div>
                <div v-else>
                  <div 
                    v-for="s in filteredStudents" 
                    :key="s.id" 
                    class="dropdown-item"
                    @click="selectStudent(s)"
                  >
                    <div class="student-info">
                      <div class="avatar-placeholder small">
                        {{ getInitials(s.name) }}
                      </div>
                      <div>
                        <div class="student-name">{{ s.name }}</div>
                        <div class="student-details">
                          <span v-if="s.srn">SRN: {{ s.srn }}</span>
                          <span v-if="s.class">Class: {{ s.class.name }}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div class="pagination-controls" v-if="totalStudentPages > 1">
                    <button 
                      class="pagination-btn" 
                      @click.stop="prevStudentPage"
                      :disabled="currentStudentPage === 1"
                    >
                      &lt;
                    </button>
                    <span class="page-info">
                      Page {{ currentStudentPage }} of {{ totalStudentPages }}
                    </span>
                    <button 
                      class="pagination-btn" 
                      @click.stop="nextStudentPage"
                      :disabled="currentStudentPage === totalStudentPages"
                    >
                      &gt;
                    </button>
                  </div>
                </div>
              </div>
            </div>
            <div class="selected-student" v-if="form.student_id" @click.stop="toggleDropdown">
              <div class="student-info">
                <div class="avatar-placeholder small">
                  {{ getInitials(selectedStudent?.name) }}
                </div>
                <div>
                  <div class="student-name">{{ selectedStudent?.name }}</div>
                  <div class="student-details">
                    <span v-if="selectedStudent?.srn">SRN: {{ selectedStudent.srn }}</span>
                    <span v-if="selectedStudent?.class">Class: {{ selectedStudent.class.name }}</span>
                  </div>
                </div>
                <button 
                  class="clear-selection" 
                  @click.stop="clearStudentSelection"
                  title="Clear selection"
                >
                  &times;
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- Rest of the form remains the same -->
        <div class="form-group">
  <label>Class</label>
  <select v-model="form.class_id" required>
    <option value="" disabled>Select Class</option>
    <option v-for="c in classes" :key="c.id" :value="c.id">
      {{ c.name }}
    </option>
  </select>
</div>

        <div class="form-group" v-if="sections.length > 0">
    <label>Section</label>
    <select v-model="form.section_id" required>
      <option value="" disabled>Select Section</option>
      <option v-for="s in sections" :key="s.id" :value="s.id">
        {{ s.name }}
      </option>
    </select>
  </div>

        <div class="form-group">
          <label>Academic Year</label>
          <select v-model="form.study_year_id" required>
            <option value="" disabled>Select Year</option>
            <option v-for="y in years" :key="y.id" :value="y.id">
              {{ y.year_study }}
            </option>
          </select>
        </div>

        <div class="form-actions">
          <button type="button" class="btn cancel-btn" @click="closeModal">
            Cancel
          </button>
          <button type="submit" class="btn submit-btn">
            {{ editingId ? 'Update' : 'Save' }}
          </button>
        </div>
      </form>
    </div>
  </div>
</div>
  </div>
</template>

<script>
import axios from 'axios';

const api = axios.create({
  baseURL: process.env.VUE_APP_API_BASE_URL || 'https://cale.caledonian.ly/api',
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('access_token');
  if (token) {
    config.headers['Authorization'] = `Bearer ${token}`;
  }
  return config;
}, (error) => Promise.reject(error));

export default {
  name: "StudentEnrollments",
  data() {
    return {
      showDropdown: false,
      enrollments: [],
      students: [],
      classes: [],
      sections: [],
      years: [],
      showModal: false,
      editingId: null,
      form: {
        student_id: '',
        class_id: '',
        section_id: '',
        study_year_id: ''
      },
      studentSearch: '',
      filteredStudents: [],
      allStudents: [],
      currentStudentPage: 1,
      totalStudentPages: 1,
      studentsPerPage: 10,
      selectedStudent: null,
    };
  },
  watch: {
    'form.class_id'(newVal) {
      if (newVal) {
        this.fetchSections(newVal);
        this.form.section_id = '';
      }
    }
  },
  async mounted() {
    await this.fetchAll();
    document.addEventListener('click', this.handleClickOutside);
  },
  beforeDestroy() {
    document.removeEventListener('click', this.handleClickOutside);
  },
  methods: {
    toggleDropdown() {
  this.showDropdown = !this.showDropdown;
  if (this.showDropdown) {
    // Clear search when dropdown opens
    this.studentSearch = '';
    this.fetchStudents();
  }
},
    getInitials(name) {
      if (!name) return '—';
      return name.split(' ').map(n => n[0]).join('').toUpperCase();
    },
    async fetchAll() {
      try {
        const [enrollmentsRes, classesRes, yearsRes] = await Promise.all([
          api.get('/student-enrollments'),
          api.get('/classes'),
          api.get('/study-years'),
        ]);
        
        this.enrollments = enrollmentsRes.data?.data ?? [];
        this.classes = classesRes.data ?? [];
        this.years = yearsRes.data?.data ?? [];
        
        // Load first page of students
        await this.fetchStudents();
      } catch (error) {
        console.error('Error fetching data:', error);
        this.$toast?.error('Failed to load data');
      }
    },

    async fetchStudents(page = 1, search = '') {
  try {
    const params = {
      page,
      per_page: this.studentsPerPage,
    };
    
    if (search && search.trim() !== '') {
      params.search = search;
    }
    
    const res = await api.get('/students', { params });
    this.filteredStudents = res.data?.students?.data ?? res.data?.students ?? [];
    this.allStudents = [...this.filteredStudents];
    this.currentStudentPage = page;
    this.totalStudentPages = res.data?.total_pages ?? 
                           Math.ceil((res.data?.total_students ?? 0) / this.studentsPerPage);
    
    // If editing, find and set the selected student
    if (this.form.student_id) {
      this.selectedStudent = this.filteredStudents.find(s => s.id === this.form.student_id);
      if (!this.selectedStudent) {
        // If not found in current page, try to find in all loaded students
        this.selectedStudent = this.allStudents.find(s => s.id === this.form.student_id);
      }
    }
  } catch (error) {
    console.error('Error fetching students:', error);
    this.$toast?.error('Failed to load students');
  }
},
    searchStudents() {
  clearTimeout(this.searchTimeout);
  this.searchTimeout = setTimeout(() => {
    if (this.studentSearch.trim() === '') {
      // If search is empty, show all students
      this.fetchStudents(1, '');
    } else {
      // Perform search
      this.fetchStudents(1, this.studentSearch);
    }
  }, 300);
},
    
    selectStudent(student) {
      this.selectedStudent = student;
      this.form.student_id = student.id;
      this.studentSearch = '';
      this.showDropdown = false; // Close dropdown after selection
    },
    clearStudentSelection() {
  this.selectedStudent = null;
  this.form.student_id = '';
  this.studentSearch = '';
  this.showDropdown = true;
  this.fetchStudents(); // Refresh the student list
},
    handleClickOutside(event) {
      if (!this.$el.contains(event.target)) {
        this.showDropdown = false;
      }
    },
    
    prevStudentPage() {
      if (this.currentStudentPage > 1) {
        this.fetchStudents(this.currentStudentPage - 1, this.studentSearch);
      }
    },
    
    nextStudentPage() {
      if (this.currentStudentPage < this.totalStudentPages) {
        this.fetchStudents(this.currentStudentPage + 1, this.studentSearch);
      }
    },
    
    async fetchSections(classId) {
  // Ensure classId is a valid number
  if (!classId || isNaN(classId)) {
    this.sections = [];
    return;
  }

  try {
    const res = await api.get(`/classes/${classId}/sections`);
    // Handle different possible response structures
    this.sections = res.data?.sections ?? res.data?.data ?? (Array.isArray(res.data) ? res.data : []);
    console.log('Fetched sections:', this.sections);
  } catch (err) {
    console.error('Failed to fetch sections:', err);
    this.$toast?.error('Failed to load sections');
    this.sections = [];
  }
},
    openModal() {
      this.showModal = true;
      this.editingId = null;
      this.resetForm();
    },
    closeModal() {
      this.showModal = false;
    },
    edit(enrollment) {
  this.editingId = enrollment.id;
  this.form = {
    student_id: enrollment.student_id,
    class_id: enrollment.class_id,
    section_id: enrollment.section_id,
    study_year_id: enrollment.study_year_id
  };
  
  // The watcher will handle fetching sections when class_id changes
  this.showModal = true;
},
    resetForm() {
      this.form = {
        student_id: '',
        class_id: '',
        section_id: '',
        study_year_id: ''
      };
      this.sections = [];
    },
    async submit() {
      try {
        if (this.editingId) {
          await api.put(`/student-enrollments/${this.editingId}`, this.form);
          this.$toast?.success('Enrollment updated successfully');
        } else {
          await api.post('/student-enrollments', this.form);
          this.$toast?.success('Student enrolled successfully');
        }
        await this.fetchAll();
        this.closeModal();
      } catch (error) {
        console.error('Error saving enrollment:', error);
        this.$toast?.error(error.response?.data?.message || 'Failed to save enrollment');
      }
    },
    async remove(id) {
      if (!confirm('Are you sure you want to delete this enrollment?')) return;
      try {
        await api.delete(`/student-enrollments/${id}`);
        this.enrollments = this.enrollments.filter(e => e.id !== id);
        this.$toast?.success('Enrollment deleted');
      } catch (err) {
        console.error('Delete failed:', err);
        this.$toast?.error('Could not delete enrollment');
      }
    }
  },
  watch: {
  'form.class_id': {
    handler(newVal) {
      if (newVal) {
        this.fetchSections(newVal);
        this.form.section_id = '';
      } else {
        this.sections = [];
      }
    },
    immediate: true
  }
},
};
</script>

<style scoped>
.container {
  padding: 2rem;
  background: #0a192f;
  color: #e6f1ff;
}

.title {
  font-size: 1.8rem;
  color: #64ffda;
  margin-bottom: 1rem;
}

.filter-bar {
  margin-bottom: 1rem;
  display: flex;
  justify-content: flex-end;
}

.table-container {
  overflow-x: auto;
}

.modern-table {
  width: 100%;
  border-collapse: collapse;
  margin-top: 1rem;
}

.modern-table th,
.modern-table td {
  padding: 0.75rem;
  border-bottom: 1px solid #1e2a47;
}

.modern-table th {
  background: #112240;
  color: #64ffda;
  text-align: left;
}

.avatar-placeholder {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: #1e2a47;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.75rem;
  color: #64ffda;
}

.btn {
  margin: 0 5px;
  padding: 6px 12px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-weight: 500;
  display: inline-flex;
  align-items: center;
}

.add-btn {
  background: #3b82f6;
  color: white;
}

.edit-btn {
  background: #2563eb;
  color: white;
}

.delete-btn {
  background: #dc2626;
  color: white;
}

.no-results {
  margin-top: 2rem;
  color: #8892b0;
  text-align: center;
  padding: 2rem;
}

/* Modal Styles */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.modal-content {
  background: #112240;
  border-radius: 8px;
  width: 90%;
  max-width: 500px;
  max-height: 90vh;
  overflow-y: auto;
  padding: 1.5rem;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
}

.modal-header h3 {
  color: #64ffda;
  margin: 0;
}

.close-btn {
  background: none;
  border: none;
  color: #8892b0;
  font-size: 1.5rem;
  cursor: pointer;
}

.form-group {
  margin-bottom: 1rem;
}

.form-group label {
  display: block;
  margin-bottom: 0.5rem;
  color: #ccd6f6;
}

.form-group select {
  width: 100%;
  padding: 0.5rem;
  border-radius: 4px;
  background: #0a192f;
  border: 1px solid #1e2a47;
  color: #e6f1ff;
}

.form-actions {
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  margin-top: 1.5rem;
}

.cancel-btn {
  background: #64748b;
  color: white;
}

.submit-btn {
  background: #3b82f6;
  color: white;
}

/* Add these new styles to your existing styles */

.student-select-container {
  position: relative;
  margin-bottom: 1rem;
}

.search-container {
  position: relative;
  margin-bottom: 0.5rem;
  cursor: pointer;
}

.search-input {
  width: 100%;
  padding: 0.5rem 2rem 0.5rem 0.75rem;
  border-radius: 4px;
  background: #0a192f;
  border: 1px solid #1e2a47;
  color: #e6f1ff;
  cursor: pointer;
}

.search-input:focus {
  outline: none;
  border-color: #3b82f6;
}

.search-icon {
  position: absolute;
  right: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  width: 1rem;
  height: 1rem;
  color: #8892b0;
  pointer-events: none;
}

.select-dropdown {
  position: absolute;
  width: 100%;
  max-height: 300px;
  overflow-y: auto;
  border: 1px solid #1e2a47;
  border-radius: 4px;
  background: #112240;
  z-index: 10;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}


.dropdown-content {
  padding: 0.5rem 0;
}

.dropdown-item {
  padding: 0.75rem;
  cursor: pointer;
  transition: background 0.2s;
}

.dropdown-item:hover {
  background: #1e2a47;
}

.student-info {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.avatar-placeholder.small {
  width: 28px;
  height: 28px;
  font-size: 0.65rem;
}

.student-name {
  font-weight: 500;
  color: #e6f1ff;
}

.student-details {
  font-size: 0.75rem;
  color: #8892b0;
  display: flex;
  gap: 0.75rem;
  margin-top: 0.25rem;
}

.selected-student {
  margin-top: 0.75rem;
  padding: 0.75rem;
  border-radius: 4px;
  background: #1e2a47;
  position: relative;
}

.clear-selection {
  position: absolute;
  right: 0.5rem;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: #8892b0;
  font-size: 1.25rem;
  cursor: pointer;
  padding: 0 0.5rem;
}

.clear-selection:hover {
  color: #e6f1ff;
}

.pagination-controls {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 0.75rem;
  border-top: 1px solid #1e2a47;
  gap: 1rem;
}

.pagination-btn {
  background: #1e2a47;
  border: none;
  color: #e6f1ff;
  width: 2rem;
  height: 2rem;
  border-radius: 4px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
}

.pagination-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.page-info {
  font-size: 0.875rem;
  color: #8892b0;
}

.no-results {
  padding: 1rem;
  text-align: center;
  color: #8892b0;
  font-size: 0.875rem;
}
</style>